(() => {
  if (customElements.get('adreno-dive-sites')) return;

  let leafletPromise;
  function loadLeaflet() {
    if (!leafletPromise) {
      const resources = [];
      leafletPromise = Promise.all([
        ['link', 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css', 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='],
        ['script', 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js', 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=']
      ].map(([tag, url, integrity]) => new Promise((resolve, reject) => {
        const node = document.createElement(tag);
        resources.push(node);
        const timer = setTimeout(() => reject(new Error('Map library timed out')), 15000);
        node.integrity = integrity;
        node.crossOrigin = 'anonymous';
        if (tag === 'link') { node.rel = 'stylesheet'; node.href = url; }
        else { node.src = url; node.async = true; }
        node.onload = () => { clearTimeout(timer); resolve(); };
        node.onerror = () => { clearTimeout(timer); reject(new Error('Map library unavailable')); };
        document.head.append(node);
      }))).then(() => window.L).catch((error) => {
        resources.forEach((node) => node.remove());
        leafletPromise = null;
        throw error;
      });
    }
    return leafletPromise;
  }

  const textElement = (tag, text) => {
    const node = document.createElement(tag);
    node.textContent = text;
    return node;
  };
  const normalise = (value) => String(value || '').toLocaleLowerCase().trim();

  class AdrenoDiveSites extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.form = this.querySelector('[data-filters]');
      this.cards = [...this.querySelectorAll('[data-site]')];
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 12);
      this.page = 1;
      this.pagination = this.querySelector('[data-pagination]');
      this.status = this.querySelector('[data-map-status]');
      this.loadButton = this.querySelector('[data-load-map]');
      this.moreButton = this.querySelector('[data-more]');
      this.mapNode = this.querySelector('[data-map]');
      this.records = [];
      this.form.hidden = false;
      this.querySelector('[data-count]').hidden = false;
      this.form.addEventListener('submit', (event) => event.preventDefault(), options);
      this.form.addEventListener('input', (event) => {
        this.page = 1;
        this.filterCards();
        if (event.target.name === 'search') this.drawRecords();
        else if (this.map) this.fetchRecords();
      }, options);
      this.form.addEventListener('reset', (event) => {
        event.preventDefault();
        ['search', 'state', 'type', 'entry'].forEach((name) => { this.form.elements[name].value = ''; });
        this.page = 1;
        this.filterCards();
        if (this.map) this.fetchRecords();
      }, options);
      this.querySelectorAll('[data-state-link]').forEach((link) => {
        link.addEventListener('click', () => {
          this.form.elements.search.value = '';
          this.form.elements.type.value = '';
          this.form.elements.entry.value = '';
          this.form.elements.state.value = link.dataset.stateLink;
          this.page = 1;
          this.filterCards();
          if (this.map) this.fetchRecords();
        }, options);
      });
      this.pagination.addEventListener('click', (event) => {
        const button = event.target.closest('[data-page]');
        if (!button || button.disabled) return;
        this.page = Number(button.dataset.page);
        this.filterCards();
        const count = this.querySelector('[data-count]');
        count.focus({ preventScroll: true });
        count.scrollIntoView({ block: 'start' });
      }, options);
      if (this.loadButton) {
        this.loadButton.hidden = false;
        this.loadButton.disabled = false;
        this.loadButton.addEventListener('click', () => this.openMap(), options);
        this.moreButton.addEventListener('click', () => this.fetchRecords(true), options);
      }
      // Selecting a block in the theme editor must reveal it even under active filters.
      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-site]');
        if (card) this.revealCard(card);
      }, options);
      window.addEventListener('hashchange', () => this.revealHash(), options);
      this.filterCards();
      this.revealHash();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.events = null;
      this.map?.remove();
      this.map = null;
    }

    filters() {
      const fields = this.form.elements;
      return { search: normalise(fields.search.value), state: fields.state.value, type: fields.type.value, entry: fields.entry.value };
    }

    filterCards() {
      const filters = this.filters();
      const matches = this.cards.filter((card) => (!filters.state || filters.state === card.dataset.state)
          && (!filters.type || filters.type === card.dataset.type)
          && (!filters.entry || filters.entry === card.dataset.entry)
          && (!filters.search || normalise(card.dataset.search).includes(filters.search)));
      const count = matches.length;
      const pages = Math.max(1, Math.ceil(count / this.pageSize));
      this.page = Math.min(Math.max(this.page, 1), pages);
      const start = (this.page - 1) * this.pageSize;
      const visible = new Set(matches.slice(start, start + this.pageSize));
      this.cards.forEach((card) => { card.hidden = !visible.has(card); });
      this.querySelectorAll('[data-state-group]').forEach((group) => {
        group.hidden = !group.querySelector('[data-site]:not([hidden])');
      });
      this.querySelector('[data-count]').textContent = count
        ? `Showing ${start + 1}–${Math.min(start + this.pageSize, count)} of ${count} featured ${count === 1 ? 'location' : 'locations'} · Page ${this.page} of ${pages}`
        : 'No featured locations found';
      this.querySelector('[data-empty]').hidden = count !== 0;
      this.pagination.replaceChildren();
      this.pagination.hidden = pages <= 1;
      if (pages > 1) {
        const addButton = (label, page, disabled = false, current = false) => {
          const button = textElement('button', label);
          button.type = 'button';
          button.dataset.page = String(page);
          button.disabled = disabled;
          if (current) button.setAttribute('aria-current', 'page');
          if (/^\d+$/.test(label)) button.setAttribute('aria-label', `Page ${page}`);
          this.pagination.append(button);
        };
        addButton('Previous', this.page - 1, this.page === 1);
        for (let page = 1; page <= pages; page++) addButton(String(page), page, false, page === this.page);
        addButton('Next', this.page + 1, this.page === pages);
      }
    }

    revealCard(card) {
      if (!this.cards.includes(card)) return;
      ['search', 'state', 'type', 'entry'].forEach((name) => { this.form.elements[name].value = ''; });
      this.page = Math.floor(this.cards.indexOf(card) / this.pageSize) + 1;
      this.filterCards();
      if (this.map) this.fetchRecords();
    }

    revealHash() {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch (_) { return; }
      const card = this.cards.find((item) => item.id === id);
      if (card) {
        this.revealCard(card);
        card.scrollIntoView({ block: 'start' });
      }
    }

    async openMap() {
      this.loadButton.disabled = true;
      this.status.textContent = 'Loading interactive map…';
      const lifecycle = this.events;
      try {
        const L = await loadLeaflet();
        if (!this.isConnected || lifecycle !== this.events) return;
        this.mapNode.hidden = false;
        if (!this.map) {
          this.map = L.map(this.mapNode, { scrollWheelZoom: true }).setView([-26, 134], 4);
          L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 18,
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
          }).addTo(this.map);
          this.markers = L.featureGroup().addTo(this.map);
        }
        this.map.invalidateSize();
        this.loadButton.hidden = true;
        await this.fetchRecords();
      } catch (_) {
        if (this.isConnected && lifecycle === this.events) {
          this.status.textContent = 'The map could not load. You can still explore every featured guide above.';
          this.loadButton.textContent = 'Try loading the map again';
        }
      } finally {
        if (lifecycle === this.events) this.loadButton.disabled = false;
      }
    }

    async fetchRecords(append = false) {
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const filters = this.filters();
      this.loadingRecords = true;
      this.recordsIncomplete = false;
      this.moreButton.hidden = true;
      this.loadButton.hidden = true;
      if (!append) {
        this.records = [];
        this.offset = 0;
        this.total = 0;
        this.markers.clearLayers();
        this.querySelector('[data-map-list]').replaceChildren();
        this.querySelector('[data-map-list-wrap]').hidden = true;
      }
      // These editorial categories have no equivalent in OpenDiveMap's taxonomy.
      if (['pier', 'cage'].includes(filters.type)) {
        this.loadingRecords = false;
        this.status.textContent = `${filters.type === 'pier' ? 'Pier and jetty' : 'Shark cage'} guides appear above. Choose another dive type to explore community map locations.`;
        return;
      }
      const params = new URLSearchParams({ country: 'AU', limit: '500' });
      if (filters.state) params.set('region', `AU-${filters.state}`);
      if (filters.type) params.set('topology', filters.type);
      if (filters.entry) params.set('entry', filters.entry);
      let timer;
      try {
        const batchSignatures = new Set();
        while (this.isConnected && this.request === controller && !controller.signal.aborted) {
          params.set('offset', String(this.offset));
          this.status.textContent = this.offset
            ? `Loading additional locations (${this.offset} of ${this.total || '…'} records)…`
            : 'Loading community dive locations…';
          // One controller owns the entire query; changing filters cancels all its batches.
          timer = setTimeout(() => controller.abort(), 15000);
          const response = await fetch(`https://api.opendivemap.com/v1/sites?${params}`, { signal: controller.signal });
          if (!response.ok) throw new Error('Site data unavailable');
          const data = await response.json();
          clearTimeout(timer);
          if (data.type !== 'FeatureCollection' || !Array.isArray(data.features)) throw new Error('Invalid site data');
          if (this.request !== controller || !this.isConnected) return;
          if (controller.signal.aborted) throw new Error('Site data timed out');
          // Stop if a broken upstream repeats the same page despite advancing offset.
          const signature = JSON.stringify(data.features.map((feature) => feature?.properties?.id));
          if (data.features.length && batchSignatures.has(signature)) throw new Error('Repeated API page');
          batchSignatures.add(signature);
          const seen = new Set(this.records.map((feature) => feature.properties.id));
          data.features.forEach((feature) => {
            const point = feature?.geometry?.coordinates;
            const properties = feature?.properties;
            if (feature?.geometry?.type !== 'Point' || !Array.isArray(point) || point.length < 2
              || !Number.isFinite(point[0]) || !Number.isFinite(point[1]) || Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90
              || properties?.country_code !== 'AU' || typeof properties?.name !== 'string' || !properties.name.trim()
              || !properties.id || seen.has(properties.id)) return;
            seen.add(properties.id);
            this.records.push(feature);
          });
          this.offset += data.features.length;
          this.total = Number.isFinite(data.numberMatched) ? data.numberMatched : this.offset;
          const hasNext = Array.isArray(data.links) && data.links.some((link) => link.rel === 'next');
          this.drawRecords();
          if (!data.features.length || (!hasNext && this.offset >= this.total)) {
            if (this.offset < this.total) throw new Error('Incomplete API page');
            break;
          }
        }
        if (this.request !== controller || !this.isConnected) return;
        this.loadingRecords = false;
        this.drawRecords();
      } catch (_) {
        if (this.request !== controller || !this.isConnected) return;
        this.loadingRecords = false;
        this.recordsIncomplete = true;
        this.status.textContent = this.records.length
          ? `Loaded ${this.records.length} locations, but the remaining site data is unavailable. Retry to finish loading.`
          : 'Community site data is unavailable right now. The featured guides above are still available.';
        if (this.offset > 0) this.moreButton.hidden = false;
        else {
          this.loadButton.textContent = 'Retry community map';
          this.loadButton.hidden = false;
        }
      } finally { clearTimeout(timer); }
    }

    drawRecords() {
      if (!this.map) return;
      const search = this.filters().search;
      const features = this.records.filter((feature) => normalise(`${feature.properties.name} ${feature.properties.sea_name || ''}`).includes(search));
      this.markers.clearLayers();
      const list = this.querySelector('[data-map-list]');
      list.replaceChildren();
      features.forEach((feature) => {
        const p = feature.properties;
        const [lng, lat] = feature.geometry.coordinates;
        const details = [];
        if (['shore', 'boat'].includes(p.entry)) details.push(`${p.entry} entry`);
        if (Number.isFinite(p.max_depth) && p.max_depth > 0) details.push(`Reported maximum depth: ${p.max_depth} m`);
        const popup = document.createElement('div');
        popup.append(textElement('strong', p.name), textElement('p', details.join(' · ') || 'Confirm site details locally.'));
        const link = textElement('a', 'View location on OpenStreetMap');
        link.href = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=13/${lat}/${lng}`;
        popup.append(link);
        const marker = window.L.marker([lat, lng], {
          title: p.name, alt: p.name,
          icon: window.L.divIcon({ className: 'ads__pin', iconSize: [16, 16], iconAnchor: [8, 8] })
        }).bindPopup(popup);
        this.markers.addLayer(marker);
        const item = document.createElement('li');
        const listLink = link.cloneNode();
        listLink.textContent = p.name;
        item.append(listLink, document.createTextNode(details.length ? ` — ${details.join(' · ')}` : ''));
        list.append(item);
      });
      this.querySelector('[data-map-list-wrap]').hidden = !features.length;
      if (features.length) this.map.fitBounds(this.markers.getBounds(), { padding: [30, 30], maxZoom: 11, animate: false });
      this.status.textContent = this.loadingRecords
        ? `Loading community locations (${this.offset} of ${this.total || '…'} records)…`
        : `${features.length} community locations shown from ${this.records.length} loaded (${this.total || 0} matching database records).${this.recordsIncomplete ? ' Some locations could not load. Retry to complete the map.' : ''}`;
    }
  }
  customElements.define('adreno-dive-sites', AdrenoDiveSites);
})();
